import { useDebouncedValue } from "~/common/hooks/useDebouncedValue";
import { type ChangeEvent, forwardRef, useCallback, useRef, useState } from "react";
import { SavingIndicator } from "~/common/components/CommentInput/components/SavingIndicator";

import { useTranslation } from "react-i18next";
import { TextField, styled } from "~/common/mui";

interface CommentProps {
  className?: string;
  value: string;
  onChange: (comment: string) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  minRows?: number;
  maxRows?: number;
}

export const useShowAndScrollInput = (toggleVisibility: () => void) => {
  const inputRef = useRef<HTMLTextAreaElement | HTMLInputElement>(null);
  const toggleCb = () => {
    toggleVisibility();
    window.setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.scrollIntoView({ block: "center" });
      }
    });
  };
  return [inputRef, toggleCb] as const;
};

const CommentHelper = styled("div")({
  display: "flex",
  justifyContent: "space-between",
});

export const CommentInput = forwardRef<HTMLTextAreaElement | HTMLInputElement, CommentProps>(
  (
    {
      value: externalValue,
      onChange: externalOnChange,
      className,
      label,
      placeholder,
      disabled,
      minRows = 3,
      maxRows = 10,
    },
    ref,
  ) => {
    const [saving, setSaving] = useState(false);
    const externalOnChangeCb = useCallback(
      async (comment: string) => {
        await externalOnChange(comment);
        setSaving(false);
      },
      [externalOnChange],
    );

    const [value, setValue] = useDebouncedValue(externalValue, externalOnChangeCb, 500);
    const { t } = useTranslation();

    const helperLabel = (
      <CommentHelper>
        {t("wishlistPage.comment.tag")}
        <SavingIndicator saving={saving} />
      </CommentHelper>
    );

    const onChange = useCallback(
      (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        setSaving(true);
        setValue(e.target.value);
      },
      [setValue],
    );

    return (
      <div className={className}>
        <TextField
          variant={"outlined"}
          label={label}
          minRows={minRows}
          maxRows={maxRows}
          inputProps={{ maxLength: 140 }}
          value={value}
          onChange={onChange}
          helperText={helperLabel}
          placeholder={placeholder}
          inputRef={ref}
          disabled={disabled}
          multiline
          fullWidth
        />
      </div>
    );
  },
);
